import { Button, IconButton, Typography } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useEnter, useEscape, useKeyPresses } from '@qfa/react-components';
import Dialog from 'components/Dialog';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useLockOverrideDialogStyles from './styles';
import { LockOverrideDialogProps } from './types';

const LockOverrideDialog: React.FunctionComponent<LockOverrideDialogProps> = (props) => {
  const { camera, lockPTZ, open, handleCloseDialog, hasPTZOverrideLockPrivilege, isOverridden } =
    props;
  const classes = useLockOverrideDialogStyles();
  const { t } = useTranslation(['video', 'common']);

  const handleClickOutsideDialog = useCallback(
    (event: MouseEvent) => {
      open && event.stopImmediatePropagation();
    },
    [open]
  );

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutsideDialog, {
      capture: true,
    });
    return () => {
      window.removeEventListener('mousedown', handleClickOutsideDialog, {
        capture: true,
      });
    };
  }, [handleClickOutsideDialog, open]);

  const handleLock = useCallback(() => {
    lockPTZ(true);
    handleCloseDialog();
  }, [handleCloseDialog, lockPTZ]);

  const onKeyUp = useKeyPresses(
    useEnter({
      callback: () => {
        hasPTZOverrideLockPrivilege && handleLock();
      },
    }),
    useEscape({ callback: handleCloseDialog })
  );

  const renderLockOverrideDialog = () => {
    return (
      <Dialog
        open={open}
        title={t('video:cameraLockOverride')}
        dialogProps={{ onKeyUp }}
        buttons={
          <>
            {!isOverridden && <Button onClick={handleCloseDialog}>{t('common:cancel')}</Button>}
            <Button
              disabled={!hasPTZOverrideLockPrivilege && !isOverridden}
              onClick={() => {
                !isOverridden && handleLock();
                isOverridden && handleCloseDialog();
              }}
              variant='contained'
              color='warning'
            >
              {t(isOverridden ? 'common:ok' : 'video:lock')}
            </Button>
          </>
        }
      >
        <div className={classes.warningDiv}>
          <IconButton className={classes.warningIconButton}>
            <WarningIcon className={classes.warningIcon} />
          </IconButton>
          <Typography variant='subtitle2' fontSize='smaller'>
            {isOverridden
              ? `${t('video:camera')} ${camera.name} ${t('video:lockedByAnotherOperator')}.`
              : `${t('video:cameraOperatedByAnother')}.`}
          </Typography>
        </div>
        <Typography variant='subtitle2'>
          {`${camera.ptzLock?.fullName || ''} ${
            isOverridden ? t('video:hasOverriddenCameraLockContol') : t('video:isControllingCamera')
          } ${camera.name}.`}
        </Typography>
        {!isOverridden && hasPTZOverrideLockPrivilege && (
          <Typography variant='subtitle2'>{t('video:controlConfirmation')}</Typography>
        )}
      </Dialog>
    );
  };

  return renderLockOverrideDialog();
};

export default LockOverrideDialog;
